// // src/components/common/Layout.tsx
// import React, { useState } from 'react';
// import { Box } from '@mui/material';
// import Navbar from './Navbar';
// import Sidebar from './Sidebar';
// import { useAuth } from '../../contexts/AuthContext';

// interface LayoutProps {
//   children: React.ReactNode;
//   showSidebar?: boolean;
// }

// const SIDEBAR_WIDTH = 260;
// const NAVBAR_HEIGHT = 64;

// const Layout: React.FC<LayoutProps> = ({ children, showSidebar = false }) => {
//   const { isAuthenticated } = useAuth();
//   const [mobileOpen, setMobileOpen] = useState(false);

//   const handleDrawerToggle = () => {
//     setMobileOpen(!mobileOpen);
//   };

//   return (
//     <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
//       <Navbar onMenuClick={showSidebar ? handleDrawerToggle : undefined} />

//       <Box 
//         sx={{ 
//           display: 'flex', 
//           flexGrow: 1, 
//           pt: `${NAVBAR_HEIGHT}px`,
//         }}
//       >
//         {isAuthenticated && showSidebar && (
//           <Sidebar open={mobileOpen} onClose={handleDrawerToggle} />
//         )}

//         <Box
//           component="main"
//           sx={{
//             flexGrow: 1,
//             width: '100%',
//             mr: {  // 👈 تغيير من ml إلى mr
//               xs: 0, 
//               md: showSidebar && isAuthenticated ? `${SIDEBAR_WIDTH}px` : 0 
//             },
//             minHeight: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
//             bgcolor: 'rgb(249, 250, 251)',
//           }}
//         >
//           {children}
//         </Box>
//       </Box>
//     </Box>
//   );
// };

// export default Layout;

// src/components/common/Layout.tsx

import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import OfflineIndicator from './OfflineIndicator';
import SyncStatusToast from './SyncStatusToast';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types';
import { ROUTES } from '../../constants';

interface LayoutProps {
  children: React.ReactNode;
  showSidebar?: boolean;
}

const NAVBAR_HEIGHT = 64;

const Layout: React.FC<LayoutProps> = ({ children, showSidebar = false }) => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const isWaiter = user?.role === UserRole.WAITER;
  const shouldShowSidebar = showSidebar && isAuthenticated && !isWaiter;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Navbar is hidden on mobile only for the public homepage
  const isHomePage = location.pathname === ROUTES.HOME;
  const isNavbarHiddenOnMobile = isHomePage && !isAuthenticated;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarCollapse = (collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
  };


  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Offline status banner moved inside main */}
      <Navbar
        onMenuClick={shouldShowSidebar ? handleDrawerToggle : undefined}
        sidebarCollapsed={sidebarCollapsed}
        showSidebar={shouldShowSidebar}
      />

      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          pt: { xs: isNavbarHiddenOnMobile ? 0 : `${NAVBAR_HEIGHT}px`, md: `${NAVBAR_HEIGHT}px` },
          width: '100%',
          height: { xs: isNavbarHiddenOnMobile ? '100vh' : `calc(100vh - ${NAVBAR_HEIGHT}px)`, md: `calc(100vh - ${NAVBAR_HEIGHT}px)` },
          overflow: 'hidden',
        }}
      >
        {shouldShowSidebar && (
          <Sidebar
            open={mobileOpen}
            onClose={handleDrawerToggle}
            onCollapse={handleSidebarCollapse}
          />
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minHeight: { xs: isNavbarHiddenOnMobile ? '100vh' : `calc(100vh - ${NAVBAR_HEIGHT}px)`, md: `calc(100vh - ${NAVBAR_HEIGHT}px)` },
            maxHeight: { xs: isNavbarHiddenOnMobile ? '100vh' : `calc(100vh - ${NAVBAR_HEIGHT}px)`, md: `calc(100vh - ${NAVBAR_HEIGHT}px)` },
            bgcolor: 'rgb(249, 250, 251)',
            overflowY: 'auto',
            overflowX: 'hidden',
            position: 'relative',
            // تحسينات التمرير
            WebkitOverflowScrolling: 'touch', // للـ iOS
            scrollbarWidth: { xs: 'none', md: 'auto' } as any,
            msOverflowStyle: { xs: 'none', md: 'auto' } as any,
            '&::-webkit-scrollbar': {
              width: { xs: 0, md: '8px' },
              display: { xs: 'none', md: 'block' },
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'rgba(0, 0, 0, 0.05)',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '4px',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.3)',
              },
            },
          }}
        >
          {/* Offline status banner — always visible at the very top of the scrollable content */}
          <OfflineIndicator />
          {children}
        </Box>
      </Box>

      {/* Global sync status toasts */}
      <SyncStatusToast />
    </Box>
  );
};

export default Layout;